<template>
  <b-card no-body class="p-2 d-flex flex-column">
    <h5 class="mb-1">Export Wizard</h5>
    <p class="mb-1">Select export parameters.</p>

    <b-form class="flex-grow-1">
      <b-form-group
        label="Data Source"
        label-cols-sm="4"
        label-for="export-index"
        class="mb-1"
      >
        <b-form-select
          id="export-index"
          v-model="selectedIndex"
          :options="indexOptions"
          :disabled="metadataLoading || !metadataLoaded || submitting"
        />
      </b-form-group>

      <b-form-group
        label="Range"
        label-cols-sm="4"
        class="mb-1 export-range-selector"
      >
        <TimeRangeSelector
          :minDate="minDate"
          :maxDate="maxDate"
          :initRange="timeRange"
          @change-time-range="handleTimeRangeChange"
        />
      </b-form-group>

      <small v-if="metadataLoading" class="text-muted d-block mb-1">
        Loading export metadata...
      </small>
      <small v-else-if="metadataError" class="text-danger d-block mb-1">
        {{ metadataError }}
      </small>
      <small class="text-muted d-block mb-0">
        Select a time range up to 30 days.
      </small>
    </b-form>

    <b-button
      variant="primary"
      class="mt-1 align-self-start"
      :disabled="submitting || !metadataLoaded"
      @click="handleExportClick"
    >
      {{ submitting ? 'Exporting...' : 'Export' }}
    </b-button>
  </b-card>
</template>

<script>
import TimeRangeSelector from '@/xvisor/components/TimeRangeSelector.vue';
import { createExport, getExportMetadata } from '@/xvisor/components/export/exportApi';
import dateCeil from '@/xvisor/utilities/dateCeil';

export default {
  components: {
    TimeRangeSelector,
  },
  data() {
    const now = dateCeil(new Date());
    const defaultRange = {
      start: new Date(now.getTime() - (24 * 60 * 60 * 1000)),
      end: now,
    };
    return {
      exportMetadataIndexes: [],
      defaultIndex: '',
      defaultTimeField: '@timestamp',
      selectedIndex: '',
      minDate: new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000)),
      maxDate: now,
      defaultRange,
      timeRange: {
        start: defaultRange.start,
        end: defaultRange.end,
      },
      metadataLoading: false,
      metadataLoaded: false,
      metadataError: '',
      submitting: false,
    };
  },
  mounted() {
    this.loadExportMetadata();
  },
  computed: {
    indexOptions() {
      if (!Array.isArray(this.exportMetadataIndexes)) return [];
      return this.exportMetadataIndexes.map((item) => ({
        value: item.name,
        text: item.name,
      }));
    },
    indexToTimeField() {
      const map = {};
      if (!Array.isArray(this.exportMetadataIndexes)) return map;
      this.exportMetadataIndexes.forEach((item) => {
        if (!item || !item.name || !item.timeField) return;
        map[item.name] = item.timeField;
      });
      return map;
    },
    selectedTimeField() {
      if (this.selectedIndex && this.indexToTimeField[this.selectedIndex]) {
        return this.indexToTimeField[this.selectedIndex];
      }
      return this.defaultTimeField || '@timestamp';
    },
  },
  methods: {
    applyExportMetadata(metadata) {
      const indexes = Array.isArray(metadata.indexes) ? metadata.indexes : [];
      this.exportMetadataIndexes = indexes
        .filter((item) => item && item.name && item.timeField)
        .map((item) => ({
          name: item.name,
          timeField: item.timeField,
        }));
      if (!this.exportMetadataIndexes.length) {
        throw new Error('No export data sources available.');
      }

      const availableNames = this.exportMetadataIndexes.map((item) => item.name);
      const responseDefaultIndex = typeof metadata.defaultIndex === 'string'
        ? metadata.defaultIndex
        : '';
      this.defaultIndex = availableNames.includes(responseDefaultIndex)
        ? responseDefaultIndex
        : this.exportMetadataIndexes[0].name;

      const responseDefaultTimeField = typeof metadata.defaultTimeField === 'string'
        ? metadata.defaultTimeField
        : '';
      this.defaultTimeField = responseDefaultTimeField
        || this.indexToTimeField[this.defaultIndex]
        || '@timestamp';

      this.selectedIndex = this.defaultIndex;
    },
    async loadExportMetadata() {
      this.metadataLoading = true;
      this.metadataLoaded = false;
      this.metadataError = '';

      try {
        const metadata = await getExportMetadata(this.$http);
        this.applyExportMetadata(metadata || {});
        this.metadataLoaded = true;
      } catch (err) {
        const responseMessage = err
          && err.response
          && err.response.data
          && typeof err.response.data.message === 'string'
          ? err.response.data.message
          : '';
        this.metadataError = responseMessage || 'Failed to load export data source metadata.';
        if (this.$bvToast) {
          this.$bvToast.toast('Failed to load export metadata.', { variant: 'danger' });
        }
      } finally {
        this.metadataLoading = false;
      }
    },
    handleTimeRangeChange(nextRange) {
      if (!nextRange || !nextRange.start || !nextRange.end) return;
      this.timeRange = {
        start: new Date(nextRange.start),
        end: new Date(nextRange.end),
      };
    },
    async handleExportClick() {
      if (this.submitting || !this.metadataLoaded) return;
      this.submitting = true;

      try {
        const payload = {
          index: this.selectedIndex,
          timeField: this.selectedTimeField,
          range: {
            start: this.timeRange.start.toISOString(),
            end: this.timeRange.end.toISOString(),
          },
        };

        const createdExport = await createExport(this.$http, payload);
        this.$emit('added', createdExport);
        if (this.$bvToast) {
          this.$bvToast.toast('Export request submitted.', { variant: 'success' });
        }
      } catch (err) {
        const responseMessage = err
          && err.response
          && err.response.data
          && typeof err.response.data.message === 'string'
          ? err.response.data.message
          : '';
        if (this.$bvToast) {
          this.$bvToast.toast(
            responseMessage || 'Failed to submit export request.',
            { variant: 'danger' },
          );
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.export-range-selector .bordered-time-range {
  float: none !important;
}
</style>
